import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

// Actions
import { actions as userSongActions } from 'modules/usersongs/actions'

// Components
import { UnderlineHeading, EmptyState, Spinner } from '@tracklib/kit'
import RevenueReport from 'components/usersong/accounting/revenuereport'
import PageHeader from 'components/usersong/pageheader'
import Totals from 'components/usersong/accounting/totals'

// Hooks
import { useRequest } from 'modules/requests/hooks'

// Types
import { Dispatch, EntityState, State, UserSong, UserSongRevenueReport } from 'types'

type AccountingProps = {
  userSong: UserSong
  userSongRevenueReportIds: Array<number>
}

const Accounting = ({ userSongRevenueReportIds, userSong }: AccountingProps): JSX.Element => {
  const dispatch = useDispatch() as Dispatch
  const userSongRevenueReports = useSelector<State, EntityState<UserSongRevenueReport>>(state => state.entities.userSongRevenueReports)
  const userSongRevenueReportsList = userSongRevenueReportIds
    .map(id => userSongRevenueReports[String(id)])
    .filter(report => !!report && report.reported)
  const fetchRevenueReportsRequest = useRequest('FETCH_REVENUE_REPORTS', `user_song_id=${userSong.id}`)
  const reportsNotLoaded = userSongRevenueReportIds.length !== userSongRevenueReportsList.length
  useEffect(() => {
    if (reportsNotLoaded && !fetchRevenueReportsRequest.isDone && !fetchRevenueReportsRequest.hasError) {
      dispatch(userSongActions.fetchRevenueReports(userSong.id))
    }
  }, []) // eslint-disable-line

  if (reportsNotLoaded) {
    if (!fetchRevenueReportsRequest.isDone) {
      return <Spinner />
    }
  }
  return (
    <>
      {userSongRevenueReportsList.length ? (
        <>
          <PageHeader title="Revenue history" subtitle="This is a complete list of all revenue reported for this song so far." />
          <Totals userSong={userSong} userSongRevenueReports={userSongRevenueReportsList} />
          {userSongRevenueReportsList
            .sort(
              (reportA, reportB) => new Date(reportB.period.reporting_open).getTime() - new Date(reportA.period.reporting_open).getTime(),
            )
            .map(({ id, reported, total_amount, revenue_streams, period: { name, reporting_open, reporting_close } }) => (
              <div key={id}>
                <UnderlineHeading heading={name} />
                <RevenueReport
                  key={id}
                  id={id}
                  userSong={userSong}
                  reported={reported}
                  totalAmount={total_amount}
                  reportingOpen={reporting_open}
                  reportingClose={reporting_close}
                  revenueStreams={revenue_streams}
                  showReportButton={false}
                />
              </div>
            ))}
        </>
      ) : (
        <EmptyState
          text="Nothing to see or do here - yet"
          subtitle="After you have licensed and released your song you will start earning royalties. When that starts to happen you need to report your revenue to us so we can distribute the income fairly."
          emoji="looking"
        />
      )}
    </>
  )
}

export default Accounting
